/**
 * @file PasswordInput.js
 * @description A reusable password input component with show/hide functionality
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getText } from '../../i18n';

/**
 * A password input field with show/hide toggle functionality
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.id] - Input field ID
 * @param {string} [props.name] - Input field name
 * @param {string} [props.value] - Input field value
 * @param {Function} props.onChange - Function to handle input changes
 * @param {string} [props.placeholder] - Input placeholder text
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled] - Whether the input is disabled
 * @param {boolean} [props.required] - Whether the input is required
 * @param {boolean} [props.autoComplete] - Whether to enable autocomplete
 * @param {string} [props.errorMessage] - Error message to display
 * @param {boolean} [props.isConfirmField] - Whether this is a confirm password field
 * @param {string} [props.originalPassword] - The original password to compare against (for confirm fields)
 * @param {boolean} [props.showMatchFeedback] - Whether to show password match feedback
 * @param {Function} [props.onFocus] - Function to handle input focus
 * @param {Function} [props.onBlur] - Function to handle input blur
 * @returns {JSX.Element} Password input with show/hide toggle
 */
const PasswordInput = ({
    id,
    name,
    value,
    onChange,
    placeholder = 'Password',
    className = '',
    disabled = false,
    required = false,
    autoComplete = 'off',
    errorMessage,
    isConfirmField = false,
    originalPassword = '',
    showMatchFeedback = true,
    onFocus,
    onBlur,
}) => {
    const [showPassword, setShowPassword] = useState(false);
    const [passwordsMatch, setPasswordsMatch] = useState(false);

    useEffect(() => {
        if (isConfirmField && showMatchFeedback) {
            setPasswordsMatch(value && value === originalPassword);
        }
    }, [value, originalPassword, isConfirmField, showMatchFeedback]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const getPasswordMatchClassName = () => {
        if (!value || !isConfirmField || !showMatchFeedback) return '';
        return passwordsMatch ? 'text-success' : 'text-danger';
    };

    const getPasswordMatchIcon = () => {
        if (!value || !isConfirmField || !showMatchFeedback) return '';
        return passwordsMatch ? 'bi-check' : 'bi-x';
    };

    const handleFocus = e => {
        if (onFocus) onFocus(e);
    };

    const handleBlur = e => {
        if (onBlur) onBlur(e);
    };

    return (
        <div className="password-input-container">
            <div className="position-relative">
                <input
                    id={id}
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control ${errorMessage ? 'is-invalid' : ''} ${className}`}
                    placeholder={placeholder}
                    name={name}
                    value={value || ''}
                    onChange={onChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    disabled={disabled}
                    required={required}
                    autoComplete={autoComplete}
                />
                <button
                    type="button"
                    className="password-toggle-btn btn position-absolute"
                    onClick={togglePasswordVisibility}
                    tabIndex="-1"
                    aria-label={
                        showPassword ? 'Hide password' : 'Show password'
                    }
                    style={{
                        right: errorMessage ? '30px' : '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        zIndex: '5',
                    }}
                >
                    <i
                        className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                    />
                </button>
            </div>

            {isConfirmField && showMatchFeedback && value && (
                <small className={`form-text ${getPasswordMatchClassName()}`}>
                    <i className={`bi ${getPasswordMatchIcon()} me-2`}></i>
                    {getText('msg_ff7d929715d7')}
                </small>
            )}

            {errorMessage && (
                <div className="invalid-feedback d-block">{errorMessage}</div>
            )}
        </div>
    );
};

PasswordInput.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    autoComplete: PropTypes.string,
    errorMessage: PropTypes.string,
    isConfirmField: PropTypes.bool,
    originalPassword: PropTypes.string,
    showMatchFeedback: PropTypes.bool,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
};

export default PasswordInput;
